/**
 * command-palette-routes.ts — Sidebar P2c support (Wave 2, 2026-05-21).
 *
 * Flattens TASK_LAYOUT into a `{path, label, group, icon, minRole}[]`
 * list for the Cmd+P / Ctrl+P command palette, plus a pure fuzzy-match
 * filter. Keeps the palette logic out of the React component so it can
 * be unit-tested.
 *
 * NOTE: Cmd+K is reserved for Ask Nexus (chat). Cmd+P is the route-jump
 * palette. Both bindings live in useKeyboardShortcuts.ts; this module
 * only provides the data + filter.
 */

import { TASK_LAYOUT } from '@/components/layout/nav-layouts';
import type { NavItem, NavGroup } from '@/components/layout/nav-layouts';
import type { UserRole } from '@/types';

export interface PaletteRoute {
  /** Route path, e.g. "/finance/bills". */
  path: string;
  /** Display label, e.g. "Bills (AP)". */
  label: string;
  /** Parent group label, e.g. "Money". */
  group: string;
  /** Icon component (passed through unchanged). */
  icon: NavItem['icon'];
  /** Minimum role required (per RoleGuard). undefined = visible to all. */
  minRole?: UserRole;
  /** True if this route's NavGroup is managerOnly (the Manager portal —
   *  see nav-layouts.ts). Comprehensive-sweep audit fix: minRole alone
   *  isn't enough to gate these — MGR_SCHEDULE/MGR_APPROVALS/etc. all
   *  have minRole:'manager' (level 5), which hasMinRole() satisfies for
   *  every role except employee, so bookkeeper/accountant/viewer would
   *  otherwise see these in palette results even though the routes are
   *  actually gated by ManagerRouteGuard (isManager || isAdmin) and
   *  dead-end for anyone else — see filterManagerOnlyGroups. */
  managerOnly?: boolean;
  /** Pre-computed lowercase search blob: `label + group + path tokens`. */
  searchBlob: string;
}

/**
 * Flatten TASK_LAYOUT into a single PaletteRoute[]. Skips entries with
 * `whenMultiEntity` flag if the caller indicates single-entity mode
 * (mirrors SideNav's filter). Returns a fresh array each call — caller
 * should memoize.
 */
export function flattenLayoutForPalette(
  layout: NavGroup[],
  options: { isMultiEntity: boolean } = { isMultiEntity: false },
): PaletteRoute[] {
  const routes: PaletteRoute[] = [];
  for (const group of layout) {
    // Skip groups with empty items (defensive — shouldn't happen post-filter).
    if (!group.items || group.items.length === 0) continue;
    for (const item of group.items) {
      // Same single-vs-multi filter SideNav uses for /consolidated etc.
      if (item.whenMultiEntity && !options.isMultiEntity) continue;
      // group.label is nullable (HOME group + similar header-less groups
      // have label=null). Coerce to '' so the palette doesn't render the
      // string "null" in the group-hint column.
      const groupLabel = group.label || '';
      routes.push({
        path: item.to,
        label: item.label,
        group: groupLabel,
        icon: item.icon,
        minRole: item.minRole,
        managerOnly: group.managerOnly,
        // Lowercase concat of label + group + path tokens for fuzzy match.
        // Splitting on / handles "/finance/bills" → ["", "finance", "bills"]
        // so a query "bills" matches the path segment.
        searchBlob: [
          item.label,
          groupLabel,
          ...item.to.split('/').filter(Boolean),
        ].join(' ').toLowerCase(),
      });
    }
  }
  return routes;
}

/**
 * Apply the user's role filter — drop any route whose minRole is above
 * the caller's role level. `hasMinRole(role)` is the same gate SideNav
 * + RoleGuard use, so visibility matches the user's actual navigation
 * capability.
 */
export function filterByRole(
  routes: PaletteRoute[],
  hasMinRole: (role: UserRole | undefined) => boolean,
): PaletteRoute[] {
  return routes.filter(r => hasMinRole(r.minRole));
}

/**
 * Comprehensive-sweep audit fix: drop managerOnly-group routes for anyone
 * who isn't a pure manager or admin/owner — mirrors ManagerRouteGuard's
 * actual gate (isManager || isAdmin), NOT SideNav's sidebar-visibility
 * filter (`role === 'manager'` only — admin/owner deliberately don't see
 * the Manager group in their OWN sidebar, per nav-layouts.ts's "owners/
 * admins don't need it, its routes stay Cmd+P-searchable" comment, but
 * they CAN reach it). Without this, minRole:'manager' (level 5) alone
 * lets bookkeeper/accountant/viewer (levels 40/30/10, all >= 5) see
 * "Team schedule" etc. as clickable palette results that then dead-end
 * on ManagerRouteGuard's permission screen.
 */
export function filterManagerOnlyGroups(
  routes: PaletteRoute[],
  canSeeManagerOnlyRoutes: boolean,
): PaletteRoute[] {
  return routes.filter(r => !r.managerOnly || canSeeManagerOnlyRoutes);
}

/**
 * Fuzzy filter routes by query. Returns up to `limit` matches.
 *
 * Match algorithm (v1, optimized for correctness over fanciness):
 *   - Empty query → return first `limit` routes (palette default state)
 *   - Otherwise: every space-split token in the query MUST appear as
 *     a substring of the route's searchBlob (case-insensitive). All
 *     tokens AND'd; no fuzzy character-skip matching.
 *
 * Ranking:
 *   - Exact label match → first
 *   - Label-prefix match → next
 *   - Label-substring match → next
 *   - Group/path match only → last
 *
 * @param routes  Flattened + role-filtered route list
 * @param query   User's typed query string
 * @param limit   Max results to return (default 8)
 */
export function filterAndRankRoutes(
  routes: PaletteRoute[],
  query: string,
  limit = 8,
): PaletteRoute[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) {
    return routes.slice(0, limit);
  }
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return routes.slice(0, limit);
  }

  // First pass: filter to routes where every token matches.
  const matched = routes.filter(r => tokens.every(t => r.searchBlob.includes(t)));

  // Second pass: rank.
  function rank(r: PaletteRoute): number {
    const lower = r.label.toLowerCase();
    // Exact label match = highest priority
    if (lower === q) return 0;
    // Label starts with query
    if (lower.startsWith(q)) return 1;
    // Label contains query as substring
    if (lower.includes(q)) return 2;
    // Group label matches
    if (r.group.toLowerCase().includes(q)) return 3;
    // Path-only match
    return 4;
  }

  // Stable sort by rank, preserving original order for ties.
  const ranked = matched
    .map((r, i) => ({ r, score: rank(r), originalIndex: i }))
    .sort((a, b) => a.score - b.score || a.originalIndex - b.originalIndex)
    .map(x => x.r);

  return ranked.slice(0, limit);
}

// Re-export TASK_LAYOUT for the consumer (CommandPalette component).
export { TASK_LAYOUT };
